import React from 'react';
import { motion } from 'framer-motion';
import SuitIcon from './SuitIcon';

const RANK_LABEL = {
  '2': '2', '3': '3', '4': '4', '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
  '10': '10', 'J': 'J', 'Q': 'Q', 'K': 'K', 'A': 'A', 'JOKER': 'JOKER',
};

/**
 * Mobile-friendly playing card:
 *   - top-left rank + small suit
 *   - one large centered suit glyph
 *   - red for hearts/diamonds, black for clubs/spades, gold for joker
 *
 * Card back shows "GK" when used for a goalkeeper slot, otherwise "K" branding.
 */
export default function PlayingCard({
  card,
  faceDown,
  highlight,
  testId,
  selected,
  small,
  gkBack,
  penaltyBack,
}) {
  if (!card && !faceDown) return null;

  if (faceDown) {
    const backLabel = penaltyBack ? '?' : gkBack ? 'GK' : 'K';
    return (
      <motion.div
        layout
        data-testid={testId}
        className={`kc-card kc-card-back relative ${highlight ? 'drawn-glow' : ''}`}
        style={{
          backgroundImage: 'url(/gameplay/card_back_default.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="font-heading" style={{
            fontSize: small ? (penaltyBack ? 20 : 14) : (penaltyBack ? 30 : 22),
            color: 'hsl(var(--k-gold))',
            letterSpacing: gkBack ? '0.05em' : '0.16em',
            textShadow: '0 0 14px hsl(var(--k-gold) / 0.6)',
          }}>
            {backLabel}
          </div>
        </div>
        <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 0 2px rgba(0,0,0,0.4)' }} />
      </motion.div>
    );
  }

  if (!card) return null;

  const isJoker = card.suit === 'joker';
  const isRed = card.suit === 'hearts' || card.suit === 'diamonds';
  const colorCls = isJoker ? 'joker' : isRed ? 'red' : 'black';
  const rankLabel = RANK_LABEL[card.rank];

  return (
    <motion.div
      layout
      initial={{ rotateY: -90, opacity: 0 }}
      animate={{ rotateY: 0, opacity: 1 }}
      transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
      data-testid={testId}
      className={`kc-card kc-card-clean ${colorCls} ${highlight ? 'drawn-glow' : ''} ${isJoker ? 'joker-shimmer' : ''}`}
    >
      {!isJoker && (
        <>
          <div className="corner-tl">
            <span className="rank">{rankLabel}</span>
            <SuitIcon suit={card.suit} className="pip" />
          </div>
          {/* PHASE 5.0A PASS 2 — mirrored BR corner so cards remain
              identifiable when the TL corner is partially occluded by
              neighbours on the crowded pitch/penalty layouts. */}
          <div className="corner-br" aria-hidden>
            <span className="rank">{rankLabel}</span>
            <SuitIcon suit={card.suit} className="pip" />
          </div>
          <div className="center-big" aria-hidden>
            {/* Deterministic SVG suit (iOS Safari ignores font-size on
                emoji-presentation ♠♥♦♣ codepoints). Sized to ~34 % of
                card width — matches TARGET screenshot proportions. */}
            <SuitIcon suit={card.suit} />
          </div>
        </>
      )}
      {isJoker && (
        <div className="absolute inset-0 flex items-center justify-center flex-col">
          <div className="font-heading" style={{ fontSize: small ? 24 : 40, lineHeight: 1, color: 'hsl(var(--k-gold-bright))', textShadow: '0 0 16px hsl(var(--k-gold-bright) / 0.95)' }}>{card.isDoubleChance ? '🎲' : '★'}</div>
          <div className="font-heading text-center px-1" style={{
            fontSize: small ? 7 : 10,
            letterSpacing: '0.16em',
            color: 'hsl(var(--k-gold))',
            lineHeight: 1.1,
            maxWidth: '92%',
          }}>
            {card.isDoubleChance ? 'DOUBLE\nCHANCE'.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            )) : 'JOKER'}
          </div>
        </div>
      )}
    </motion.div>
  );
}
